import {
  type PointerEventHandler,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMediaQuery } from "../../foundations/useMediaQuery.js";
import {
  anchorOffsetForWidth,
  clamp,
  clampToViewport,
  cornerCursor,
  distance,
  isTap,
  type LiveBreakPipLayout,
  midpoint,
  PIP_CORNERS,
  PIP_MAX_WIDTH_PX,
  PIP_MIN_WIDTH_PX,
  PIP_PHONE_MIN_WIDTH_PX,
  PIP_VIEWPORT_MARGIN_PX,
  type PipCorner,
  type PipPoint,
  pinchWidth,
  resizeWidth,
  widthBounds,
} from "./pipGestures.js";

/** One resize corner's handle. */
export interface PipCornerHandle {
  cursor: string;
  /** False while the frame can neither grow nor shrink: the handle takes no pointer. */
  isActive: boolean;
  onPointerDown: PointerEventHandler<HTMLElement>;
}

export interface PipFrameControls {
  containerRef: RefObject<HTMLElement | null>;
  /** During a drag, resize or pinch: transitions stop and the player takes no pointer. */
  isInteracting: boolean;
  /** The main input is a touch screen: the frame shrinks further, and touches on it never scroll. */
  isCoarsePointer: boolean;
  /** 1 unless a pinch is under way, which scales the frame until it lifts and commits a width. */
  pinchScale: number;
  /** For the whole frame: one pointer drags it, two pinch it. Controls and the player keep theirs. */
  onSurfacePointerDown: PointerEventHandler<HTMLElement>;
  resizeHandles: Record<PipCorner, PipCornerHandle>;
}

/** Presses on these act, never drag. */
const NON_DRAG_SELECTOR = 'button, a, input, [role="button"], [role="menuitem"]';
/** Each corner handle carries this attribute; a press on one resizes instead. */
const RESIZE_HANDLE_SELECTOR = "[data-pip-resize-handle]";

interface DragGesture {
  kind: "pending" | "drag";
  id: number;
  start: PipPoint;
  natural: PipPoint;
  startOffset: PipPoint;
  size: { width: number; height: number };
  inset: Insets;
}

interface PinchGesture {
  kind: "pinch";
  ids: [number, number];
  startDistance: number;
  startWidth: number;
  startOffset: PipPoint;
  rectLeft: number;
  rectBottom: number;
  bounds: { min: number; max: number };
}

type Gesture = DragGesture | PinchGesture;

interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/** The safe-area insets in pixels, read through a probe since `env()` has no value to read directly. */
function readSafeAreaInsets(): Insets {
  if (typeof document === "undefined") return NO_INSETS;
  const probe = document.createElement("div");
  probe.style.cssText =
    "position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)";
  document.body.appendChild(probe);
  const style = getComputedStyle(probe);
  const read = (value: string) => Number.parseFloat(value) || 0;
  const insets = {
    top: read(style.paddingTop),
    right: read(style.paddingRight),
    bottom: read(style.paddingBottom),
    left: read(style.paddingLeft),
  };
  probe.remove();
  return insets;
}

function viewportSize() {
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

/** The width range for a frame at `rect`, which grows up and to the right from its bottom-left corner. */
function anchoredWidthBounds(rect: DOMRect, minWidth: number) {
  const viewport = viewportSize();
  const inset = readSafeAreaInsets();
  return widthBounds(
    viewport.width - rect.left - PIP_VIEWPORT_MARGIN_PX - inset.right,
    rect.bottom - PIP_VIEWPORT_MARGIN_PX - inset.top,
    minWidth,
  );
}

/** The layout pulled back inside the viewport (a smaller window, a rotated phone); null when it fits. */
function layoutInsideViewport(
  rect: DOMRect,
  layout: LiveBreakPipLayout,
  minWidth: number,
): LiveBreakPipLayout | null {
  const viewport = viewportSize();
  const inset = readSafeAreaInsets();
  const m = PIP_VIEWPORT_MARGIN_PX;
  const clamped = clampToViewport({
    desired: { x: rect.left, y: rect.top },
    size: { width: rect.width, height: rect.height },
    viewport,
    inset,
  });
  const left = Math.max(m + inset.left, clamped.x);
  const top = Math.max(m + inset.top, clamped.y);
  const offset = { x: layout.offset.x + left - rect.left, y: layout.offset.y + top - rect.top };
  let width = layout.width;
  if (width !== null) {
    const { min, max } = widthBounds(
      viewport.width - left - m - inset.right,
      viewport.height - top - m - inset.bottom,
      minWidth,
    );
    width = clamp(width, min, max);
  }
  const moved = offset.x !== layout.offset.x || offset.y !== layout.offset.y;
  return moved || width !== layout.width ? { offset, width } : null;
}

/** Listens for one pointer's moves and its end on the document; the teardown runs once. */
function trackPointer(
  pointerId: number,
  onMove: (event: PointerEvent) => void,
  onEnd: () => void,
): () => void {
  let done = false;
  const teardown = () => {
    if (done) return;
    done = true;
    document.removeEventListener("pointermove", move);
    document.removeEventListener("pointerup", end);
    document.removeEventListener("pointercancel", end);
    onEnd();
  };
  const move = (event: PointerEvent) => {
    if (event.pointerId === pointerId) onMove(event);
  };
  const end = (event: PointerEvent) => {
    if (event.pointerId === pointerId) teardown();
  };
  document.addEventListener("pointermove", move);
  document.addEventListener("pointerup", end);
  document.addEventListener("pointercancel", end);
  return teardown;
}

const NOOP: PointerEventHandler<HTMLElement> = () => {};

interface ContainerRect {
  top: number;
  right: number;
  width: number;
}

/**
 * The picture-in-picture frame's drag, corner resize and pinch (boxed's `usePipFrame`), on a
 * layout the app keeps: every move reports the next layout through `onLayoutChange`. One pointer
 * drags from anywhere but a control or the player; two pinch, scaling the frame until they lift
 * and then committing its width. The frame moves by its offset, a transform, and never reparents
 * its player, which would restart the stream.
 */
export function usePipFrame({
  layout,
  onLayoutChange,
  enabled = true,
}: {
  layout: LiveBreakPipLayout;
  onLayoutChange: (layout: LiveBreakPipLayout) => void;
  /** Off, the frame stays where the flow puts it: no drag, resize or pinch, and no viewport bounds. */
  enabled?: boolean;
}): PipFrameControls {
  const containerRef = useRef<HTMLElement | null>(null);
  const layoutRef = useRef(layout);
  const onChangeRef = useRef(onLayoutChange);
  useLayoutEffect(() => {
    layoutRef.current = layout;
    onChangeRef.current = onLayoutChange;
  });
  const commit = useCallback((next: LiveBreakPipLayout) => {
    layoutRef.current = next;
    onChangeRef.current(next);
  }, []);

  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  const minWidth = isCoarsePointer ? PIP_PHONE_MIN_WIDTH_PX : PIP_MIN_WIDTH_PX;
  const [isInteracting, setIsInteracting] = useState(false);
  const [containerRect, setContainerRect] = useState<ContainerRect | null>(null);
  const [pinchScale, setPinchScale] = useState(1);
  const pinchScaleRef = useRef(1);
  const pointers = useRef(new Map<number, PipPoint>());
  const gesture = useRef<Gesture | null>(null);
  const removeListeners = useRef<(() => void) | null>(null);
  const resizeTeardown = useRef<(() => void) | null>(null);

  const setScale = useCallback((next: number) => {
    pinchScaleRef.current = next;
    setPinchScale(next);
  }, []);

  const setInteracting = useCallback((next: boolean) => {
    setIsInteracting(next);
    document.body.style.userSelect = next ? "none" : "";
  }, []);

  // A gesture in flight when the frame closes lets go of its listeners and the page's styles.
  useEffect(
    () => () => {
      removeListeners.current?.();
      resizeTeardown.current?.();
      document.body.style.userSelect = "";
    },
    [],
  );

  // Measured whenever a gesture ends (a drag moves the frame without resizing it) and on resize,
  // so the corners' cursors match, and the layout is pulled back inside a viewport that shrank.
  useEffect(() => {
    if (isInteracting || !enabled) return;
    const node = containerRef.current;
    if (!node) return;
    let frame: number | null = null;
    const measure = () => {
      frame = null;
      const rect = node.getBoundingClientRect();
      setContainerRect({ top: rect.top, right: rect.right, width: rect.width });
      const inside = layoutInsideViewport(rect, layoutRef.current, minWidth);
      if (inside) commit(inside);
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(node);
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [isInteracting, enabled, minWidth, commit]);

  const dragFrom = useCallback((id: number, start: PipPoint): DragGesture | null => {
    const node = containerRef.current;
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    const startOffset = layoutRef.current.offset;
    return {
      kind: "pending",
      id,
      start,
      natural: { x: rect.left - startOffset.x, y: rect.top - startOffset.y },
      startOffset,
      size: { width: rect.width, height: rect.height },
      inset: readSafeAreaInsets(),
    };
  }, []);

  const pinchFrom = useCallback(
    (ids: [number, number]): PinchGesture | null => {
      const node = containerRef.current;
      const a = pointers.current.get(ids[0]);
      const b = pointers.current.get(ids[1]);
      if (!node || !a || !b) return null;
      const rect = node.getBoundingClientRect();
      return {
        kind: "pinch",
        ids,
        startDistance: distance(a, b),
        startWidth: rect.width,
        startOffset: layoutRef.current.offset,
        rectLeft: rect.left,
        rectBottom: rect.bottom,
        bounds: anchoredWidthBounds(rect, minWidth),
      };
    },
    [minWidth],
  );

  const onMove = useCallback(
    (event: PointerEvent) => {
      if (!pointers.current.has(event.pointerId)) return;
      pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const current = gesture.current;
      if (!current) return;
      if (current.kind === "pinch") {
        const a = pointers.current.get(current.ids[0]);
        const b = pointers.current.get(current.ids[1]);
        if (!a || !b) return;
        const next = pinchWidth(
          current.startWidth,
          current.startDistance,
          distance(a, b),
          current.bounds,
        );
        setScale(next / current.startWidth);
        commit({
          ...layoutRef.current,
          offset: anchorOffsetForWidth({
            offset: current.startOffset,
            rectLeft: current.rectLeft,
            rectBottom: current.rectBottom,
            focus: midpoint(a, b),
            startWidth: current.startWidth,
            nextWidth: next,
          }),
        });
        return;
      }
      if (event.pointerId !== current.id) return;
      const now = { x: event.clientX, y: event.clientY };
      if (current.kind === "pending") {
        if (isTap(current.start, now)) return;
        gesture.current = { ...current, kind: "drag" };
        setInteracting(true);
      }
      const placed = clampToViewport({
        desired: {
          x: current.natural.x + current.startOffset.x + (now.x - current.start.x),
          y: current.natural.y + current.startOffset.y + (now.y - current.start.y),
        },
        size: current.size,
        viewport: viewportSize(),
        inset: current.inset,
      });
      commit({
        ...layoutRef.current,
        offset: { x: placed.x - current.natural.x, y: placed.y - current.natural.y },
      });
    },
    [commit, setInteracting, setScale],
  );

  const onUp = useCallback(
    (event: PointerEvent) => {
      if (!pointers.current.delete(event.pointerId)) return;
      const current = gesture.current;
      const [remaining] = [...pointers.current.keys()];
      if (current?.kind === "pinch") {
        commit({
          ...layoutRef.current,
          width: Math.round(current.startWidth * pinchScaleRef.current),
        });
        setScale(1);
      }
      if (remaining !== undefined) {
        // One finger of a pinch lifted: the other carries on as a drag from where it is.
        gesture.current = null;
        requestAnimationFrame(() => {
          const point = pointers.current.get(remaining);
          if (!point || gesture.current) return;
          const next = dragFrom(remaining, point);
          gesture.current = next && { ...next, kind: "drag" };
        });
        return;
      }
      gesture.current = null;
      setInteracting(false);
      removeListeners.current?.();
      removeListeners.current = null;
    },
    [commit, dragFrom, setInteracting, setScale],
  );

  const onSurfacePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (resizeTeardown.current !== null) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(RESIZE_HANDLE_SELECTOR)) return;
      if (pointers.current.size === 0 && target?.closest(NON_DRAG_SELECTOR)) return;
      if (pointers.current.size >= 2) return;
      event.preventDefault();
      const point = { x: event.clientX, y: event.clientY };
      pointers.current.set(event.pointerId, point);
      if (!removeListeners.current) {
        document.addEventListener("pointermove", onMove);
        document.addEventListener("pointerup", onUp);
        document.addEventListener("pointercancel", onUp);
        removeListeners.current = () => {
          document.removeEventListener("pointermove", onMove);
          document.removeEventListener("pointerup", onUp);
          document.removeEventListener("pointercancel", onUp);
        };
      }
      if (pointers.current.size === 1) {
        gesture.current = dragFrom(event.pointerId, point);
        return;
      }
      const [first, second] = [...pointers.current.keys()];
      if (first === undefined || second === undefined) return;
      gesture.current = pinchFrom([first, second]);
      setInteracting(true);
    },
    [enabled, dragFrom, onMove, onUp, pinchFrom, setInteracting],
  );

  const startResize = useCallback(
    (corner: PipCorner, event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (resizeTeardown.current || gesture.current || pointers.current.size > 0) return;
      const node = containerRef.current;
      if (!node) return;
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const rect = node.getBoundingClientRect();
      const start = { x: event.clientX, y: event.clientY };
      const bounds = anchoredWidthBounds(rect, minWidth);
      setInteracting(true);
      resizeTeardown.current = trackPointer(
        event.pointerId,
        (move) =>
          commit({
            ...layoutRef.current,
            width: resizeWidth({
              corner,
              startWidth: rect.width,
              start,
              now: { x: move.clientX, y: move.clientY },
              bounds,
            }),
          }),
        () => {
          setInteracting(false);
          resizeTeardown.current = null;
        },
      );
    },
    [commit, minWidth, setInteracting],
  );

  const resizeHandles = useMemo(() => {
    const viewportWidth = typeof window === "undefined" ? 0 : viewportSize().width;
    // It grows up and to the right from its bottom-left corner, so growing needs room on both.
    const canExpand =
      containerRect !== null &&
      containerRect.width < PIP_MAX_WIDTH_PX &&
      containerRect.top > PIP_VIEWPORT_MARGIN_PX &&
      containerRect.right < viewportWidth - PIP_VIEWPORT_MARGIN_PX;
    const canShrink = containerRect !== null && containerRect.width > minWidth;
    const isActive = enabled && (canExpand || canShrink);
    return Object.fromEntries(
      PIP_CORNERS.map((corner) => [
        corner,
        {
          cursor: cornerCursor(corner, canExpand, canShrink),
          isActive,
          onPointerDown: isActive
            ? (event: ReactPointerEvent<HTMLElement>) => startResize(corner, event)
            : NOOP,
        },
      ]),
    ) as Record<PipCorner, PipCornerHandle>;
  }, [enabled, containerRect, minWidth, startResize]);

  return {
    containerRef,
    isInteracting,
    isCoarsePointer,
    pinchScale,
    onSurfacePointerDown,
    resizeHandles,
  };
}
